import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Order } from '@hero/schema';
import { TableCell } from '@hero/web-components';
import { PlusIcon } from '@hero/web-icons';

import { Button } from '../../../../../ui/Button/Button';
import { formatPlateForDisplay } from '../../../../../app/Select/SelectAsync/selectorEntityRowUtils';
import { OrderDriverPickerDrawer } from '../../../Drawer/OrderDriverPickerDrawer';
import { OrderSelectedDriverAndVehicleDrawer } from '../../../Drawer/OrderSelectedDriverAndVehicleDrawer';

export interface DriverCellProps {
  order: Order;
  className?: string;
}

/**
 * Driver column cell. Mirrors the Price column UX, with two distinct
 * click destinations depending on assignment state:
 *
 *   - **No driver assigned** → render an "Add driver" CTA. Tap opens
 *     `OrderDriverPickerDrawer` (search + tap-to-pick, mirroring the
 *     `LocationPickerDrawer` shell). Picking a driver who has no
 *     bound vehicle chains into the vehicle picker so the row is
 *     fully configured before the order PATCH lands.
 *   - **Driver already assigned** → show their name + vehicle plate.
 *     Tap opens `OrderSelectedDriverAndVehicleDrawer`, a focused detail
 *     surface that surfaces driver + vehicle + legal docs and offers
 *     a "Change driver" CTA which loops back into the picker. We
 *     intentionally don't push the user straight into the picker
 *     here — most clicks on an assigned driver are "let me check the
 *     license / plate", not "let me reassign".
 *
 * The cell stops click propagation so opening either drawer does not
 * also fire the row's "open order drawer" handler.
 */
export const DriverCell: React.FC<DriverCellProps> = ({ order, className }) => {
  const { t } = useTranslation(['m-order', 'm-vehicle']);
  const [isOpen, setIsOpen] = useState(false);

  const stop = (e: React.MouseEvent) => e.stopPropagation();
  const open = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsOpen(true);
  };

  if (!order.driver) {
    return (
      <TableCell className={className} onClick={stop}>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={open}
          className="h-auto px-2 py-1 -ms-2 text-primary">
          <PlusIcon className="w-4 h-4 me-1" />
          {t('table.driver_cell.add_driver', { defaultValue: 'Add driver' })}
        </Button>
        <OrderDriverPickerDrawer
          order={order}
          isOpen={isOpen}
          onClose={() => setIsOpen(false)}
        />
      </TableCell>
    );
  }

  const plateLabel =
    formatPlateForDisplay(order.vehicle?.registration?.plate) ||
    t('m-vehicle:no_plate', { defaultValue: 'No plate' });

  return (
    <TableCell className={className} onClick={stop}>
      <button
        type="button"
        onClick={open}
        className="flex flex-col gap-0.5 text-start text-gray-600 body2 cursor-pointer hover:text-primary focus:outline-none">
        <span className="font-medium text-sm">{order.driver.name || 'N/A'}</span>
        <span className="text-xs text-muted-foreground">
          <span dir="ltr">{plateLabel}</span>
        </span>
      </button>
      <OrderSelectedDriverAndVehicleDrawer
        order={order}
        isOpen={isOpen}
        onClose={() => setIsOpen(false)}
      />
    </TableCell>
  );
};
